import * as React from 'react';
import { RootState } from '../reducers/reducers';
import { connect, ConnectedProps } from 'react-redux';
import { InfoCircleOutlined } from '@ant-design/icons';
import styles from './styles/stepStyles';
import { runwayColors } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Layout from 'antd/lib/layout/layout';
import Select from 'antd/lib/select';
import {
    selectAppSettingAutomations,
    selectRelease,
    selectReleaseStep,
    selectFiltersAndFixesCount,
} from '../selectors/selectors';
import FeatureReadinessItems from './featureReadiness/FeatureReadinessItems';
import { AutomationType, ReleaseStepFeatureReadinessData, ReleaseTimelinePhase } from '../types';
import PercentCompleteComponent from './featureReadiness/PercentCompleteComponent';
import { FormInstance } from 'antd/lib/form';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import { getAutomationIdByType, getFeatureItemsIgnoredCount } from '@/helpers';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import {
    selectFilteredSortedDoneItemsForStepRun,
    selectFilteredSortedPendingWorkItemsForStepRun,
} from '@/selectors/memoizedSelectors';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';

interface State {
    workingBranchEnabled?: boolean;
    appliedFiltersCount: number;
}

class StepContentFeatureReadiness extends React.Component<Props, State> {
    filtersForm: React.RefObject<FormInstance>;

    constructor(props) {
        super(props);

        this.state = {
            workingBranchEnabled: null,
            appliedFiltersCount: this.props.appliedFiltersCount,
        };
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (props.showBranchSelectionField && !props.releaseBranchExists && state.workingBranchEnabled === null) {
            // default to showing items from working branch when release branch doesn't exist

            return {
                ...state,
                workingBranchEnabled: true,
            };
        }

        return null;
    }

    render() {
        return (
            <Layout
                style={{
                    ...styles.innerContent,
                    ...styles.spaceBetween,
                    minWidth: 1000,
                    paddingTop: 0,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        flexShrink: 0,
                        paddingBottom: 65,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: 40,
                            paddingBottom: 12,
                        }}
                    >
                        <div style={{ marginRight: 36 }}>
                            <PercentCompleteComponent
                                workingBranchEnabled={this.state.workingBranchEnabled}
                                percentComplete={
                                    this.state.workingBranchEnabled ?
                                        this.props.workingBranchPercentComplete
                                    :   this.props.percentComplete
                                }
                                appliedFiltersCount={this.props.appliedFiltersCount}
                            />
                        </div>
                        <Space direction="vertical">
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                {this.props.showBranchSelectionField && (
                                    <Space direction="vertical" style={{ marginRight: 24 }}>
                                        <Space>
                                            <Text strong>Base branch</Text>
                                            <Tooltip title="View progress of items of work against the selected branch">
                                                <InfoCircleOutlined style={{ color: runwayColors.info }} />
                                            </Tooltip>
                                        </Space>
                                        <Space>
                                            <Select
                                                onChange={(value) => {
                                                    const workingBranchEnabled = value === this.props.workingBranch;

                                                    this.setState({
                                                        ...this.state,
                                                        workingBranchEnabled,
                                                    });
                                                }}
                                                defaultValue={
                                                    this.props.releaseBranchExists ?
                                                        this.props.releaseBranch
                                                    :   this.props.workingBranch
                                                }
                                                style={{ minWidth: 300 }}
                                                disabled={this.props.disableBranchSelectionField}
                                            >
                                                <Select.Option
                                                    value={this.props.releaseBranch}
                                                    disabled={!this.props.releaseBranchExists}
                                                >
                                                    {this.props.releaseBranch}
                                                    {!this.props.releaseBranchExists && ' (not created)'}
                                                </Select.Option>
                                                <Select.Option value={this.props.workingBranch}>
                                                    {this.props.workingBranch}
                                                </Select.Option>
                                            </Select>
                                        </Space>
                                    </Space>
                                )}
                            </div>
                            {this.state.workingBranchEnabled && (
                                <Text style={{ color: runwayColors.statusYellow }}>
                                    {this.props.releaseBranchExists ?
                                        'Release branch not selected'
                                    :   'Release branch not created'}
                                </Text>
                            )}
                        </Space>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                paddingLeft: 24,
                                flexGrow: 1,
                            }}
                        ></div>
                        <div style={{ marginTop: '-40px' }}>
                            <ReleaseEventsTimelineSection
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                stepRunId={this.props.stepRunComponentProps?.stepRunId}
                                context={ReleaseTimelineSectionContext.ReleaseStep}
                                releaseStepType={this.props.releaseStepType}
                            />
                        </div>
                    </div>
                    <FeatureReadinessItems
                        releaseStepType={this.props.releaseStepType}
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        releaseId={this.props.releaseId}
                        stepRunId={this.props.stepRunId}
                        workingBranchEnabled={
                            this.state.workingBranchEnabled === null ? false : this.state.workingBranchEnabled
                        }
                        percentComplete={
                            this.state.workingBranchEnabled ?
                                this.props.workingBranchPercentComplete
                            :   this.props.percentComplete
                        }
                        userId={this.props.userId}
                        latestStagingBuild={this.props.latestStagingBuild}
                        selectedStagingBuild={this.props.selectedStagingBuild}
                        latestWorkingBranchBuild={this.props.latestWorkingBranchBuild}
                        appStoreSelectedBuilds={this.props.appStoreSelectedBuilds}
                        autoCherryPickAutomationId={this.props.autoCherryPickAutomationId}
                        autoMergeRunwayPRsAutomationId={this.props.autoMergeRunwayPRsAutomationId}
                        autoDownloadArtifactsAutomationId={this.props.autoDownloadArtifactsAutomationId}
                        autoUploadDsymsToStabilityMonitoringAutomationId={
                            this.props.autoUploadDsymsToStabilityMonitoringAutomationId
                        }
                        autoUploadBinaryArtifactToAppStoreAutomationId={
                            this.props.autoUploadBinaryArtifactToAppStoreAutomationId
                        }
                        stepRunComponentProps={this.props.stepRunComponentProps}
                    />
                </div>
            </Layout>
        );
    }
}

const mapDispatch = (_) => {
    return {};
};

const mapProps = (state: RootState, ownProps: ReleaseStepOrStepRunComponentProps) => {
    const { orgId, appId, releaseId, releaseStepType } = ownProps;

    const release = selectRelease(state, releaseId);
    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType);
    const stepRunId = releaseStep.id;
    const stepData = releaseStep.data as ReleaseStepFeatureReadinessData;

    const workingBranch = stepData.workingBranch;
    const userId = state.user.id;
    const releaseBranch = stepData.releaseBranch;
    const releaseBranchExists = stepData.doesBranchExist;

    const showBranchSelectionField =
        (releaseBranchExists && releaseBranch !== workingBranch) ||
        (!!workingBranch && workingBranch !== releaseBranch && release.timelinePhase === ReleaseTimelinePhase.Current);

    const backwardsCompatibleStepRunId = generateBackwardsCompatibleReleaseStepRunId(releaseId, releaseStepType);

    const appliedFiltersCount = selectFiltersAndFixesCount(state);
    const calculatePercentComplete = (workingBranch: boolean) => {
        const filteredSortedPendingItems = selectFilteredSortedPendingWorkItemsForStepRun(
            state,
            backwardsCompatibleStepRunId,
            workingBranch,
        );
        const filteredSortedDoneItems = selectFilteredSortedDoneItemsForStepRun(
            state,
            backwardsCompatibleStepRunId,
            workingBranch,
        );

        const doneItemsIgnoredCount = getFeatureItemsIgnoredCount(filteredSortedDoneItems);
        const pendingItemsIgnoredCount = getFeatureItemsIgnoredCount(filteredSortedPendingItems);
        const doneCountNotIgnored = filteredSortedDoneItems.length - doneItemsIgnoredCount;
        const itemsCountNotIgnored =
            filteredSortedDoneItems.length +
            filteredSortedPendingItems.length -
            (pendingItemsIgnoredCount + doneItemsIgnoredCount);

        if (itemsCountNotIgnored > 0) {
            const percentage = (100 * doneCountNotIgnored) / itemsCountNotIgnored;

            // Special handling to avoid rounding to 100 misleadingly
            if (percentage > 99 && percentage < 100) {
                return 99;
            }

            return Math.round(percentage);
        }

        return 0;
    };

    const disableBranchSelectionField = !(
        release.timelinePhase === ReleaseTimelinePhase.Current ||
        (release.timelinePhase === ReleaseTimelinePhase.Upcoming && stepData.workingBranchItems?.length > 0)
    );
    const autoUploadDsymsToStabilityMonitoringAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoUploadDsymsToStabilityMonitoring,
    );

    const autoUploadBinaryArtifactToAppStoreAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoUploadBinaryArtifactToAppStore,
    );

    const autoDownloadArtifactsAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoDownloadArtifacts,
    );

    const autoCherryPickAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoCherryPickFromWorkingBranch,
    );

    const automations = selectAppSettingAutomations(state, appId);

    // TODO: #WORKFLOWS# this is a temporary fix, what is the best way to get automation ids that apply to multiple steps?
    const autoMergeRunwayPRsAutomationId = automations.find(
        (automation) => automation.type === AutomationType.AutoMergeRunwayPRs,
    )?.id;

    return {
        orgId,
        appId,
        userId,
        stepRunId,
        releaseId,
        stepRunComponentProps: ownProps.stepRunComponentProps,
        latestStagingBuild: stepData.latestStagingBuild,
        selectedStagingBuild: stepData.selectedStagingBuild,
        latestWorkingBranchBuild: stepData.latestWorkingBranchBuild,
        appStoreSelectedBuilds: stepData.appStoreSelectedBuilds,
        autoDownloadArtifactsAutomationId,
        autoUploadDsymsToStabilityMonitoringAutomationId,
        autoUploadBinaryArtifactToAppStoreAutomationId,
        autoCherryPickAutomationId,
        autoMergeRunwayPRsAutomationId,
        releaseStepType,
        workingBranch,
        releaseBranch,
        percentComplete: calculatePercentComplete(false),
        workingBranchPercentComplete: calculatePercentComplete(true),
        releaseBranchExists,
        showBranchSelectionField,
        disableBranchSelectionField,
        appliedFiltersCount,
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(StepContentFeatureReadiness);
